import AltRouteIcon from "@mui/icons-material/AltRouteOutlined";
import ChecklistIcon from "@mui/icons-material/ChecklistOutlined";
import DeveloperBoardIcon from "@mui/icons-material/DeveloperBoardOutlined";
import FactCheckIcon from "@mui/icons-material/FactCheckOutlined";
import GppGoodIcon from "@mui/icons-material/GppGoodOutlined";
import LinearScaleIcon from "@mui/icons-material/LinearScaleOutlined";
import SchemaIcon from "@mui/icons-material/SchemaOutlined";
import ThermostatIcon from "@mui/icons-material/ThermostatOutlined";
import { Box, ButtonBase, Typography, alpha } from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useProjectStore } from "@stores/projectStore";
import { SUGGESTIONS } from "@utils/constants";
import { type ElementType, useState } from "react";

interface SuggestionChipsProps {
  /** Called immediately on click — the welcome screen wires this to
   *  ChatContainer.handleSend so the prompt fires the moment the chip
   *  is pressed (no double-click tax for the first turn). */
  onSelect: (prompt: string) => void;
}

const ICON_MAP: Record<string, ElementType> = {
  architecture: SchemaIcon,
  bom: ChecklistIcon,
  power: AltRouteIcon,
  mcu: DeveloperBoardIcon,
  bus: LinearScaleIcon,
  drc: FactCheckIcon,
  thermal: ThermostatIcon,
  protection: GppGoodIcon,
};

export function SuggestionChips({ onSelect }: SuggestionChipsProps) {
  const activeProject = useProjectStore((s) => s.activeProject);
  const isStreaming = useChatStore((s) => s.isStreaming);
  // Latch which chip the user pressed so the others fade out instantly
  // — a small visual that says "got it, sending" without waiting for
  // the SSE stream to land. Cleared once isStreaming flips to true
  // (the streaming message itself takes over the visual feedback).
  const [pressedLabel, setPressedLabel] = useState<string | null>(null);

  const handleClick = (label: string, prompt: string) => {
    // Guard against fast double-click while a previous turn is still
    // in flight — the upstream useChat send-mutation also no-ops when
    // streaming, but this prevents a brief flash of two pressed chips.
    if (pressedLabel || isStreaming) return;
    const final = activeProject
      ? prompt.replace("this project", `'${activeProject}'`)
      : prompt;
    setPressedLabel(label);
    onSelect(final);
  };

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr 1fr",
          sm: "repeat(2, 1fr)",
          md: "repeat(4, 1fr)",
        },
        gap: 1.25,
        width: "100%",
        maxWidth: 720,
        mt: 3,
        mx: "auto",
        // Staggered reveal — each chip slides in 60ms behind the
        // previous, layered on top of the welcome screen's own block-
        // level stagger. The combined effect is one orchestrated
        // page-load sequence rather than a wall of UI appearing at
        // once.
        "& > *": {
          opacity: 0,
          animation: "cf-chip-rise 380ms ease-out forwards",
        },
        "& > *:nth-of-type(1)": { animationDelay: "440ms" },
        "& > *:nth-of-type(2)": { animationDelay: "500ms" },
        "& > *:nth-of-type(3)": { animationDelay: "560ms" },
        "& > *:nth-of-type(4)": { animationDelay: "620ms" },
        "& > *:nth-of-type(5)": { animationDelay: "680ms" },
        "& > *:nth-of-type(6)": { animationDelay: "740ms" },
        "& > *:nth-of-type(7)": { animationDelay: "800ms" },
        "& > *:nth-of-type(8)": { animationDelay: "860ms" },
        "@keyframes cf-chip-rise": {
          from: { opacity: 0, transform: "translateY(6px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {SUGGESTIONS.map(({ label, prompt, icon }) => {
        const Icon = ICON_MAP[icon] ?? SchemaIcon;
        const isPressed = pressedLabel === label;
        const isDimmed = pressedLabel !== null && !isPressed;
        return (
          <ButtonBase
            key={label}
            disabled={isStreaming || pressedLabel !== null}
            onClick={() => handleClick(label, prompt)}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.75,
              p: 1.75,
              borderRadius: 2,
              bgcolor: isPressed ? "accent.soft" : "surface.raised",
              border: "1px solid",
              borderColor: isPressed ? "primary.main" : "border.default",
              textAlign: "left",
              opacity: isDimmed ? 0.35 : 1,
              transition: (t) =>
                `border-color ${t.motion.fast}, background-color ${t.motion.fast}, transform ${t.motion.fast}, box-shadow ${t.motion.fast}, opacity ${t.motion.fast}`,
              ...(isPressed && {
                boxShadow: (t) =>
                  `0 0 0 1px ${alpha(t.palette.primary.main, 0.5)}, 0 0 18px ${alpha(t.palette.primary.main, 0.4)}`,
                transform: "translateY(-1px)",
              }),
              "&:hover": {
                borderColor: "border.accent",
                bgcolor: "accent.softer",
                transform: "translateY(-1px)",
                boxShadow: (t) =>
                  `0 4px 14px -8px ${alpha(t.palette.primary.main, 0.55)}`,
                "& .suggestion-icon-bg": {
                  bgcolor: "accent.soft",
                  borderColor: "primary.main",
                },
              },
              "&:focus-visible": {
                borderColor: "primary.main",
              },
              "&.Mui-disabled": {
                cursor: pressedLabel ? "wait" : "not-allowed",
              },
            }}
          >
            <Box
              className="suggestion-icon-bg"
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.25,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "accent.softer",
                border: "1px solid",
                borderColor: "border.accent",
                transition: (t) =>
                  `background-color ${t.motion.fast}, border-color ${t.motion.fast}`,
              }}
            >
              <Icon sx={{ fontSize: 16, color: "primary.light" }} />
            </Box>
            <Typography
              sx={{
                fontSize: "0.8125rem",
                color: "text.primary",
                fontWeight: 500,
                lineHeight: 1.3,
              }}
            >
              {label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
